<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocaleSwitch, type SupportedLocale } from '@/composables/useLocaleSwitch';

const { locale, t } = useI18n();
const { switchLocale } = useLocaleSwitch();

const isOpen = ref(false);

const ariaLabel = computed(() => {
  try {
    return t('locale.current');
  } catch {
    return 'Select language';
  }
});

const locales: Array<{ code: SupportedLocale; flag: string }> = [
  { code: 'en', flag: '🇺🇸' },
  { code: 'es', flag: '🇪🇸' },
  { code: 'pt', flag: '🇧🇷' },
];

const localizedLocales = computed(() => {
  return locales.map((item) => ({
    ...item,
    name: t(`locale.${item.code}`),
  }));
});

const currentLocaleData = computed(() => {
  return localizedLocales.value.find((l) => l.code === locale.value) || localizedLocales.value[1];
});

const availableLocales = computed(() => {
  return localizedLocales.value.filter((l) => l.code !== locale.value);
});

function toggleDropdown() {
  isOpen.value = !isOpen.value;
}

function closeDropdown() {
  isOpen.value = false;
}

async function selectLocale(code: SupportedLocale) {
  await switchLocale(code);
  closeDropdown();
}

// Keyboard navigation
function handleKeydown(event: KeyboardEvent) {
  if (!isOpen.value) return;

  switch (event.key) {
    case 'Escape':
      closeDropdown();
      break;
    case 'ArrowDown':
      event.preventDefault();
      break;
    case 'ArrowUp':
      event.preventDefault();
      break;
  }
}

// Click outside to close
function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.language-switcher')) {
    closeDropdown();
  }
}

// Add/remove click outside listener properly
onMounted(() => {
  window.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="language-switcher relative" @keydown="handleKeydown">
    <button
      type="button"
      class="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-noir-300 hover:text-gold-400 hover:bg-noir-800/50 transition-colors"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="ariaLabel"
      @click="toggleDropdown"
    >
      <span class="text-lg" aria-hidden="true">{{ currentLocaleData.flag }}</span>
      <span class="hidden sm:inline">{{ currentLocaleData.code.toUpperCase() }}</span>
      <svg
        class="w-4 h-4 transition-transform"
        :class="{ 'rotate-180': isOpen }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <!-- Dropdown -->
    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <ul
        v-if="isOpen"
        class="absolute right-0 mt-2 w-48 rounded-lg bg-noir-800 border border-noir-700 shadow-lg py-1 z-50"
        role="listbox"
        :aria-label="ariaLabel"
      >
        <li
          v-for="lang in availableLocales"
          :key="lang.code"
          role="option"
          :aria-selected="lang.code === locale"
          class="flex items-center gap-3 px-4 py-2.5 text-sm text-cream-100 hover:bg-gold-500/10 hover:text-gold-400 cursor-pointer transition-colors"
          @click="selectLocale(lang.code)"
        >
          <span class="text-lg" aria-hidden="true">{{ lang.flag }}</span>
          <span>{{ lang.name }}</span>
        </li>
      </ul>
    </transition>
  </div>
</template>
